import {
  TextChatAttachmentPolicy,
  TextChatAvailability,
  TextChatAvailabilityStatus,
  TextChatCapabilities,
  TextChatMode,
  TextChatModeContract,
  TextChatThemeContract,
  TextChatThemeVariant,
} from "./TextChat.types";

const commonValidationMessageKeys = {
  tooManyFiles: "textChatTooManyFiles",
  fileTooLarge: "textChatFileTooLarge",
  unsupportedType: "textChatUnsupportedFileType",
};

const documentMimeTypes = [
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/xml",
];

const documentFileExtensions = [".txt", ".doc", ".docx", ".xml"];
const audioMimeTypes = [
  "audio/webm",
  "audio/mp4",
  "audio/mpeg",
  "audio/wav",
  "audio/ogg",
  "audio/aac",
  "audio/flac",
];
const audioFileExtensions = [".mp3", ".wav", ".ogg", ".m4a", ".aac", ".flac", ".webm"];

export const TEXT_CHAT_THEME_BY_VARIANT: Record<
  TextChatThemeVariant,
  TextChatThemeContract
> = {
  inbox: {
    variant: "inbox",
    surface: "var(--chat-surface, var(--card-bg))",
    surfaceMuted: "var(--chat-surface-elevated, var(--bg-secondary))",
    composer: "var(--chat-surface-elevated, var(--input-bg))",
    border: "var(--chat-border, var(--border-color))",
    text: "var(--chat-text, var(--text-primary))",
    textMuted: "var(--chat-text-muted, var(--text-secondary))",
    accent: "var(--primary-color)",
    accentHover: "var(--primary-hover)",
    currentUserMessage: "var(--my-message-bg)",
    otherUserMessage: "var(--chat-surface-elevated, var(--bg-tertiary))",
    shadow: "var(--shadow-sm)",
    transition: "var(--transition-fast)",
    easing: "var(--ease-smooth)",
    overlayZIndex: "var(--z-modal)",
    containerRadius: "20px",
  },
  compactCall: {
    variant: "compactCall",
    surface: "var(--bg-primary)",
    surfaceMuted: "var(--bg-secondary)",
    composer: "var(--input-bg)",
    border: "var(--border-color)",
    text: "var(--text-primary)",
    textMuted: "var(--text-secondary)",
    accent: "var(--primary-color)",
    accentHover: "var(--primary-hover)",
    currentUserMessage: "var(--my-message-bg)",
    otherUserMessage: "var(--bg-tertiary)",
    shadow: "var(--shadow-sm)",
    transition: "var(--transition-fast)",
    easing: "var(--ease-smooth)",
    overlayZIndex: "var(--z-modal)",
    containerRadius: "20px",
  },
  liveEvent: {
    variant: "liveEvent",
    surface: "var(--card-bg)",
    surfaceMuted: "var(--bg-secondary)",
    composer: "var(--input-bg)",
    border: "var(--border-color)",
    text: "var(--text-primary)",
    textMuted: "var(--text-secondary)",
    accent: "var(--primary-color)",
    accentHover: "var(--primary-hover)",
    currentUserMessage: "var(--my-message-bg)",
    otherUserMessage: "var(--bg-tertiary)",
    shadow: "var(--shadow-sm)",
    transition: "var(--transition-fast)",
    easing: "var(--ease-smooth)",
    overlayZIndex: "var(--z-modal)",
    containerRadius: "20px",
  },
};

export const TEXT_CHAT_AVAILABILITY_BY_STATUS: Record<
  TextChatAvailabilityStatus,
  TextChatAvailability
> = {
  available: {
    status: "available",
    canWrite: true,
  },
  muted: {
    status: "muted",
    canWrite: true,
    messageKey: "textChatMuted",
  },
  blockedByMe: {
    status: "blockedByMe",
    canWrite: false,
    messageKey: "textChatBlockedByMe",
  },
  blockedByAnotherUser: {
    status: "blockedByAnotherUser",
    canWrite: false,
    messageKey: "textChatBlockedByAnotherUser",
  },
  expired: {
    status: "expired",
    canWrite: false,
    messageKey: "textChatExpired",
  },
  disabled: {
    status: "disabled",
    canWrite: false,
    messageKey: "textChatDisabled",
  },
  unavailable: {
    status: "unavailable",
    canWrite: false,
    messageKey: "textChatUnavailable",
  },
  reconnecting: {
    status: "reconnecting",
    canWrite: false,
    messageKey: "textChatReconnecting",
  },
  left: {
    status: "left",
    canWrite: false,
    messageKey: "textChatLeft",
  },
};

export const GLOBAL_TEXT_CHAT_ATTACHMENT_POLICY: TextChatAttachmentPolicy = {
  enabled: true,
  acceptedMimeTypes: ["image/*", ...documentMimeTypes, ...audioMimeTypes],
  acceptedFileExtensions: [...documentFileExtensions, ...audioFileExtensions],
  maxFiles: 5,
  maxFileSizeBytes: 50 * 1024 * 1024,
  allowPaste: true,
  allowDrop: true,
  validationMessageKeys: commonValidationMessageKeys,
};

export const SESSION_TEXT_CHAT_ATTACHMENT_POLICY: TextChatAttachmentPolicy = {
  enabled: true,
  acceptedMimeTypes: ["image/*", ...documentMimeTypes],
  acceptedFileExtensions: documentFileExtensions,
  maxFiles: 1,
  maxFileSizeBytes: 5 * 1024 * 1024,
  allowPaste: false,
  allowDrop: false,
  validationMessageKeys: commonValidationMessageKeys,
};

export const WEBINAR_TEXT_CHAT_ATTACHMENT_POLICY: TextChatAttachmentPolicy = {
  enabled: false,
  acceptedMimeTypes: [],
  acceptedFileExtensions: [],
  maxFiles: 0,
  maxFileSizeBytes: 0,
  allowPaste: false,
  allowDrop: false,
  validationMessageKeys: commonValidationMessageKeys,
};

export const TEXT_CHAT_CAPABILITIES_BY_MODE: Record<
  TextChatMode,
  TextChatCapabilities
> = {
  global: {
    conversationList: true,
    conversationSearch: true,
    unreadCounts: true,
    historyPagination: true,
    attachments: true,
    emojiPicker: true,
    reactions: true,
    readReceipts: true,
    muteConversation: true,
    blockConversation: true,
    leaveConversation: true,
    participantActions: true,
    directChatIntent: true,
    voiceMessages: true,
  },
  session: {
    conversationList: false,
    conversationSearch: false,
    unreadCounts: false,
    historyPagination: false,
    attachments: true,
    emojiPicker: false,
    reactions: true,
    readReceipts: false,
    muteConversation: false,
    blockConversation: false,
    leaveConversation: false,
    participantActions: false,
    directChatIntent: false,
    voiceMessages: false,
  },
  webinar: {
    conversationList: false,
    conversationSearch: false,
    unreadCounts: false,
    historyPagination: false,
    attachments: false,
    emojiPicker: false,
    reactions: false,
    readReceipts: false,
    muteConversation: false,
    blockConversation: false,
    leaveConversation: false,
    participantActions: false,
    directChatIntent: false,
    voiceMessages: false,
  },
};

export const TEXT_CHAT_MODE_CONTRACTS: Record<
  TextChatMode,
  TextChatModeContract
> = {
  global: {
    mode: "global",
    themeVariant: "inbox",
    capabilities: TEXT_CHAT_CAPABILITIES_BY_MODE.global,
    attachmentPolicy: GLOBAL_TEXT_CHAT_ATTACHMENT_POLICY,
    defaultAvailability: TEXT_CHAT_AVAILABILITY_BY_STATUS.available,
  },
  session: {
    mode: "session",
    themeVariant: "compactCall",
    capabilities: TEXT_CHAT_CAPABILITIES_BY_MODE.session,
    attachmentPolicy: SESSION_TEXT_CHAT_ATTACHMENT_POLICY,
    defaultAvailability: TEXT_CHAT_AVAILABILITY_BY_STATUS.available,
  },
  webinar: {
    mode: "webinar",
    themeVariant: "liveEvent",
    capabilities: TEXT_CHAT_CAPABILITIES_BY_MODE.webinar,
    attachmentPolicy: WEBINAR_TEXT_CHAT_ATTACHMENT_POLICY,
    defaultAvailability: TEXT_CHAT_AVAILABILITY_BY_STATUS.available,
  },
};

export const getTextChatModeContract = (
  mode: TextChatMode,
): TextChatModeContract => TEXT_CHAT_MODE_CONTRACTS[mode];

export const getTextChatTheme = (
  variant: TextChatThemeVariant,
): TextChatThemeContract => TEXT_CHAT_THEME_BY_VARIANT[variant];
